import * as React from 'react';
import {validateBirthDate} from '@practicegenius/domain/src/data/users/birthdate';
import {
  calculateProgress,
  PrimaryActionButtonProps,
  primaryActionButtonProps,
  SecondaryActionButtonProps,
  secondaryActionButtonProps,
} from './utils';
import * as registrationMachine from '../machines/accountRegistration';
import lang from '../lang';
import {UseMachineType} from '../machines/useAccountRegistrationMachineType';

type Props = {
  registrationMachine: UseMachineType;
};

type ScreenProps = Props & {
  lang: typeof lang.registerBirthday;
  onFormSubmit: React.ComponentProps<'form'>['onSubmit'];
  progress: number;
  actionButtons: {
    primary: PrimaryActionButtonProps;
    secondary: SecondaryActionButtonProps;
  };
  birthdateInputProps: {
    value: number | Date | null;
    onChange: (date: Date | null) => unknown;
    onBlur: () => unknown;
    error: string | undefined;
  };
  firstName: string | null;
};

export type RegisterBirthdayScreenType = React.ComponentType<ScreenProps>;

export function RegisterBirthday(
  props: Props & {children: RegisterBirthdayScreenType}
) {
  const [
    {
      context: {navigator, account, practiceUserRecord, validationErrors},
      matches,
    },
    send,
  ] = props.registrationMachine;

  const progress = calculateProgress({
    draftAccount: account,
    location: navigator.location,
  });

  const birthdate = account.birthdate ?? practiceUserRecord.birthdate;

  const actionButtons = {
    primary: primaryActionButtonProps({
      children: lang.registerBirthday.buttons.primary.title,
      loading: matches({
        [registrationMachine.RegistrationState.birthdate]:
          registrationMachine.RegistrationState.submittingBirthdate,
      }),
      disabled: Boolean(!birthdate || validationErrors?.account?.birthdate),
    }),
    secondary: secondaryActionButtonProps({
      children: lang.registerBirthday.buttons.secondary.title,
      onClick: () => navigator.goBack(),
      loading: false,
      disabled: false,
    }),
  };

  const renderProps: ScreenProps = {
    ...props,
    lang: lang.registerBirthday,
    onFormSubmit: e => {
      e.preventDefault();
      send({
        type: registrationMachine.RegistrationEvent.UPDATE_REGISTRATION,
        payload: {birthdate},
      });
    },
    progress,
    actionButtons,
    birthdateInputProps: {
      value: birthdate,
      onChange: (date: Date | null) => {
        send({
          type: registrationMachine.RegistrationEvent.UPDATE_DATA,
          payload: {account: {birthdate: date}},
        });
      },
      onBlur: () => {
        const validationError = validateBirthDate(birthdate ?? null);

        if (validationError) {
          send({
            type: registrationMachine.RegistrationEvent.HAS_VALIDATION_ERRORS,
            errors: {account: {birthdate: validationError}},
          });
        }
      },
      error: validationErrors?.account?.birthdate,
    },
    firstName: account.firstname,
  };

  const Screen = props.children;
  return <Screen {...renderProps} />;
}
